import { getCoverageLinesText } from '@common/utils';
import { isEmpty } from 'lodash';
import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { Chip, Link, MenuItem, Stack, Typography } from '@common-components';
import { AutoAwesomeOutlined } from '@icons';
import { CoverageLine, DuplicationResult } from 'enums';
import { messages } from 'i18n';
import { CarrierSubmission } from 'types';
import { getTimezoneLessDisplayDate } from 'utils';
import FormSelect from 'components/hookFormComponents/FormSelect';
import { generateSubmissionQueueDetailsPath } from 'broker/broker-routes';
import { SubmissionViewMode } from 'broker/components/SubmissionsView/utils';
import { submissionStatusConfig } from 'broker/configuration-mappers/submission-status-config';

const { fields, clearance } = messages.carrierSubmissionWorkspace;

const allStatusOptions = [
  { value: DuplicationResult.Cleared, label: clearance.statusCleared },
  { value: DuplicationResult.Duplicate, label: clearance.statusDuplicate },
  { value: DuplicationResult.Error, label: clearance.statusError },
];

const editableStatusOptions = allStatusOptions.filter((option) => option.value !== DuplicationResult.Error);

interface ClearanceStatusSectionProps {
  carrierSubmission: CarrierSubmission;
}

function ReadOnlyField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={0.5} mb={2}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

export default function ClearanceStatusSection({ carrierSubmission }: ClearanceStatusSectionProps) {
  const { watch } = useFormContext();
  const currentStatus = watch('duplicationStatus') as DuplicationResult | '';

  const duplication = carrierSubmission.clearanceDetails?.duplication;
  const matchInfo = duplication?.matchedSubmissionInfo;

  const matchedInsuredName = matchInfo?.matchedInsuredName ?? clearance.unknownInsuredName;

  if (!carrierSubmission.clearanceDetails) {
    return (
      <Typography variant="body2" color="text.secondary">
        {clearance.noClearanceData}
      </Typography>
    );
  }

  const isInternalSource = matchInfo?.source === 'internal';
  const isDuplicate = currentStatus === DuplicationResult.Duplicate;
  const isError = currentStatus === DuplicationResult.Error;

  const selectOptions = isError ? allStatusOptions : editableStatusOptions;

  return (
    <Stack gap={2}>
      {/* Appointment Check — coming soon */}
      <Stack gap={1}>
        <Typography variant="body2" fontWeight={600}>
          {clearance.appointmentCheck}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} p={2} bgcolor="blueGrey.50" borderRadius={2}>
          <AutoAwesomeOutlined color="primary" fontSize="small" />
          <Typography variant="body2" color="primary">
            {messages.carrierSubmissionWorkspace.comingSoon}
          </Typography>
        </Stack>
      </Stack>

      {/* Duplication status — editable */}
      <Stack gap={1}>
        <FormSelect name="duplicationStatus" id="duplicationStatus" label={fields.duplicationStatus}>
          {selectOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </FormSelect>
      </Stack>

      {/* Match details — read-only */}
      {isDuplicate && matchInfo && (
        <Stack gap={0.5} p={2} bgcolor="grey.50" borderRadius={2}>
          <ReadOnlyField label={fields.source}>
            <Stack direction="row">
              <Chip
                size="small"
                label={isInternalSource ? clearance.sourceInternal : clearance.sourceExternal}
                color={isInternalSource ? 'info' : 'default'}
                variant="outlined"
              />
            </Stack>
          </ReadOnlyField>

          <ReadOnlyField label={fields.matchedSubmission}>
            {isInternalSource ? (
              <Link
                target="_blank"
                href={generateSubmissionQueueDetailsPath(matchInfo.matchedSubmissionId)}
                underline="hover"
                variant="body2"
              >
                {matchedInsuredName}
              </Link>
            ) : (
              <Typography variant="body2">{matchedInsuredName}</Typography>
            )}
          </ReadOnlyField>

          <ReadOnlyField label={fields.matchedStatus}>
            <Typography variant="body2">
              {submissionStatusConfig[matchInfo.matchedSubmissionStatus].text(SubmissionViewMode.General)}
            </Typography>
          </ReadOnlyField>

          {matchInfo.matchedEffectiveDate && (
            <ReadOnlyField label={fields.matchedEffectiveDate}>
              <Typography variant="body2">{getTimezoneLessDisplayDate(matchInfo.matchedEffectiveDate)}</Typography>
            </ReadOnlyField>
          )}

          {!isEmpty(matchInfo.matchedCoverageLines) && (
            <ReadOnlyField label={fields.matchedCoverageLines}>
              <Typography variant="body2">
                {getCoverageLinesText(matchInfo.matchedCoverageLines as CoverageLine[])}
              </Typography>
            </ReadOnlyField>
          )}

          <ReadOnlyField label={fields.matchReason}>
            <Typography variant="body2">{matchInfo.matchReason}</Typography>
          </ReadOnlyField>
        </Stack>
      )}

      {isError && duplication?.errorReason && (
        <Stack gap={0.5} p={2} bgcolor="grey.50" borderRadius={2}>
          <ReadOnlyField label={fields.errorReason}>
            <Typography variant="body2">{duplication.errorReason}</Typography>
          </ReadOnlyField>
        </Stack>
      )}
    </Stack>
  );
}
